import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  InfoItem,
  ModalService,
  ResponsiveService,
  SimpleModalConfig,
} from '@frk/eds-components';
import { CartHandlerService } from '@literature/services/cart-handler.service';
import { AppStateService } from '@services/app-state.service';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { TableResponsiveService } from '@shared/table/table-responsive.service';
import { TranslateService } from '@shared/translate/translate.service';
import { from, Observable, Subject } from 'rxjs';
import { finalize, map, takeUntil } from 'rxjs/operators';
import { LiteratureYourCartMailConfirmOrderService } from './services/literature-your-cart-mail-confirm-order.service';
import cloneDeep from 'lodash/cloneDeep';
import {
  LiteratureDocument,
  LiteratureDocumentListingState,
  ModalId,
  MailRequestBody,
  CartDetails,
  MailFormValues,
} from '@types';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { Logger } from '@utils/logger';
import { StateDropdownItems } from '@literature/const/literature-data-const';
import { StorageService } from '@services';
import { FIRM_TOKEN } from '@utils/app.constants';
const logger = Logger.getLogger('LiteratureYourCartMailConfirmOrderComponent');

@Component({
  selector: 'ft-literature-your-cart-mail-confirm-order',
  templateUrl: './literature-your-cart-mail-confirm-order.component.html',
  styleUrls: ['./literature-your-cart-mail-confirm-order.component.scss'],
  providers: [TableResponsiveService],
})
export class LiteratureYourCartMailConfirmOrderComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy {
  // for progress stepper
  steps = [
    this.translateService.instant('literature.lit-mail-stepper-step1'),
    this.translateService.instant('literature.lit-mail-stepper-step2'),
  ];
  current = 1;
  isHandheld$: Observable<boolean>;
  isMobile$: Observable<boolean>;
  orderFormDetailsValue: MailFormValues;
  cartItems$: Observable<LiteratureDocumentListingState>;
  cards: InfoItem[][] = [];
  mailRequestBody: MailRequestBody;
  operationType: string;
  stateDropdownItems = StateDropdownItems;
  stateName: string;
  public isSubmitOrderClicked = false;

  @ViewChild('tableResponsive', { static: false })
  public set table(table: ElementRef) {
    if (table) {
      this.tableResponsiveService?.init(table);
    }
  }
  private unsubscribe$: Subject<void> = new Subject<void>();

  public isPrimericaUser = false;

  constructor(
    private responsiveService: ResponsiveService,
    private literatureYourCartMailConfirmOrderService: LiteratureYourCartMailConfirmOrderService,
    private cartService: CartHandlerService,
    private changeDetectorRef: ChangeDetectorRef,
    private modalService: ModalService,
    private appStateService: AppStateService,
    private translateService: TranslateService,
    protected tableResponsiveService: TableResponsiveService,
    protected literatureConfig: LiteratureGlobalConfigService,
    private storageService: StorageService
  ) {
    super();
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.isMobile$ = this.responsiveService.isMobile$();
    this.tableResponsiveService.cards$
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((cards) => {
        this.cards = cards;
        this.changeDetectorRef.detectChanges();
      });
  }

  ngOnInit(): void {
    const firmToken$ = from(this.storageService.retrieve(FIRM_TOKEN));

    this.setBreadcrumbItems();
    this.orderFormDetailsValue = this.literatureYourCartMailConfirmOrderService.getMailFormValue();
    this.stateDropdownItems.forEach((state) => {
      if (state.value === this.orderFormDetailsValue?.stateCode) {
        this.stateName = state.label;
      }
    });
    this.cartItems$ = this.cartService
      .getCartItems()
      .pipe(
        map((data: LiteratureDocumentListingState) =>
          this.checkRules(cloneDeep(data))
        )
      );

    firmToken$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((firmToken: string) => {
        this.isPrimericaUser = firmToken === 'partner-primerica';
      });
  }

  getOverAllQty(cartItems: LiteratureDocument[]): number {
    return this.cartService.getOverAllQuantity(cartItems);
  }

  goToLiterature() {
    this.literatureConfig.navigateToUrl(
      this.literatureConfig.navigationUrls?.litListing
    );
  }

  checkRules(
    data: LiteratureDocumentListingState
  ): LiteratureDocumentListingState {
    this.cartService.checkCheckoutEligibilty(data);

    return data;
  }
  placeOrder(cartItems: LiteratureDocumentListingState): void {
    this.isSubmitOrderClicked = true;
    const cartDetails: CartDetails[] = [];
    cartItems.cartItems.forEach((cartItem: LiteratureDocument) => {
      if (cartItem.quantity > 0) {
        cartDetails.push({
          literatureCode: cartItem.docId,
          qty: cartItem.quantity,
        });
      }
    });
    this.mailRequestBody = {
      ...this.literatureYourCartMailConfirmOrderService.getMailFormValue(),
      cartItems: [...cartDetails],
    };
    this.literatureYourCartMailConfirmOrderService
      .postMail(this.mailRequestBody)
      .pipe(
        finalize(() =>
          this.literatureConfig.navigateToUrl(
            this.literatureConfig.navigationUrls?.orderSuccess
          )
        )
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (response) => {
          this.literatureYourCartMailConfirmOrderService.setPostMailResponse(
            response
          );
        },
        (errorResponse) => {
          if (errorResponse?.status === 404) {
            logger.warn(
              '[postMail] ' +
                errorResponse?.messaage +
                ' code: ' +
                errorResponse?.error?.error?.errorList[0]?.code
            );
          }
          this.literatureYourCartMailConfirmOrderService.setPostMailError(
            errorResponse.error
          );
        }
      );
  }

  removeAndGetLinkValue(item): void {
    this.tableResponsiveService.removeValue(item);
  }

  editData(event: MouseEvent): void {
    event.preventDefault();
    this.literatureConfig.navigateToUrl(
      this.literatureConfig.navigationUrls?.orderCheckoutAddress
    );
  }

  disableAction(event: MouseEvent): void {
    event.preventDefault();
  }

  navigateToMailAdress(): void {
    this.literatureConfig.navigateToUrl(
      this.literatureConfig.navigationUrls?.orderCheckoutAddress
    );
  }

  openConfirmMoadal(event: MouseEvent, operationType: string): void {
    event.preventDefault();
    this.operationType = operationType;
    this.openModal('cartModal');
  }

  openModal(modelId: string, modalTitle?: string): void {
    const config: SimpleModalConfig = {
      modalId: modelId,
      // closeBtnLabel: this.modalCloseBtnLabel,
      title: modalTitle,
      type: 'simple',
      body: '',
      themeTitle: 'dark',
      preTitle: '',
      eventName: 'lightbox_display',
      eventAction: modalTitle,
    };
    this.modalService.open(modelId, config);
  }

  closeModal(modelId: string, event: MouseEvent): void {
    this.modalService.close(modelId);
  }

  cancelOrder(): void {
    this.modalService.close('cartModal');
    if (this.operationType === 'emptyCart') {
      this.cartService.emptyCartStorage();
    }
    this.literatureConfig.navigateToUrl(
      this.literatureConfig.navigationUrls?.litOrderCheckoutUrl
    );
  }

  /**
   * get literature details page link
   * @param url string
   * @returns string
   */
  getLiteratureDetailLink(docId: string): string {
    return `${this.literatureConfig?.navigationUrls?.litDetailUrl}/${docId}`;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
